<template>
  <div class="w-full grid space-y-2">
    <div class="space-y-1">
      <label for="search" class="block text-sm font-medium text-white">{{ __('Search') }}</label>
      <div class="flex items-center">
        <x-input :icon="iconSearch" v-model="search" type="text" name="search" id="search"/>
      </div>
    </div>

    <Listbox as="div" v-model="limit" v-slot="{ open }">
      <ListboxLabel class="block text-sm font-medium text-white">{{ __('Limit') }}</ListboxLabel>
      <div class="relative mt-1">
        <ListboxButton
            class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
        >
          <div v-if="iconFilter" class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <component :is="iconFilter" class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <span class="block truncate pl-7">{{ limit }} {{ __('per Page') }}</span>
          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpIcon :class="{'rotate-180': open}" class="h-5 w-5 text-gray-400 transition-all ease-in-out duration-300" aria-hidden="true"/>
          </span>
        </ListboxButton>

        <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100"
                    leave-to-class="opacity-0">
          <ListboxOptions
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            <ListboxOption as="template" v-for="limited in [25,50,100,250,500]" :key="limited" :value="limited"
                           v-slot="{ active, selected }">
              <li :class="[active ? 'bg-gray-100' : 'text-gray-900', limit == limited ? 'before:border-l-2 before:border-primary before:rounded-r-md before:h-6 before:left-0 relative before:absolute' : '', 'cursor-default select-none py-2 px-4']">
                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{
                    limited
                  }} {{ __('per Page') }}</span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>

  </div>
</template>

<script>
import {Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions} from '@headlessui/vue'
import {ChevronUpIcon, CheckIcon} from '@heroicons/vue/solid'
import XInput from "@/Jetstream/Input.vue";
import {defineComponent,h} from "vue";

export default {
  components: {
    ListboxButton,
    ListboxLabel,
    Listbox,
    XInput,
    ListboxOption,
    ListboxOptions,
    ChevronUpIcon,
    CheckIcon,
  },
  props: {
    searchKey: {
      type: String,
      default: 'search'
    },
    limitKey: {
      type: Number,
      default: 'limit',
    },
    pageKey: {
      type: String,
      default: 'page',
    },
    defaultLimit: {
      type: Number,
      default: 25,
    },
    waitUntilSearch: {
      type: Number,
      default: 1400,
    },
    waitUntilLimit: {
      type: Number,
      default: 1400,
    },
    preserveState: {
      type: Boolean,
      default: true,
    },
    iconSearch: {
      default: defineComponent({
        render() {
          return h('svg', {
            width: '24',
            height: '24',
            viewBox: '0 0 24 24',
            fill: 'none',
            xmlns: 'http://www.w3.org/2000/svg'
          }, [
            h('path', {
              d: 'M21 21L15.0001 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z',
              stroke: 'currentColor',
              'stroke-width': '2',
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round'
            })
          ]);
        }
      })
    },
    iconFilter: {
      default: defineComponent({
        render() {
          return h('svg', {
            width: '24',
            height: '24',
            viewBox: '0 0 24 24',
            fill: 'none',
            xmlns: 'http://www.w3.org/2000/svg'
          }, [
            h('path', {
              d: 'M2 4.6C2 4.03995 2 3.75992 2.10899 3.54601C2.20487 3.35785 2.35785 3.20487 2.54601 3.10899C2.75992 3 3.03995 3 3.6 3H20.4C20.9601 3 21.2401 3 21.454 3.10899C21.6422 3.20487 21.7951 3.35785 21.891 3.54601C22 3.75992 22 4.03995 22 4.6V5.26939C22 5.53819 22 5.67259 21.9672 5.79756C21.938 5.90831 21.8901 6.01323 21.8255 6.10776C21.7526 6.21443 21.651 6.30245 21.4479 6.4785L15.0521 12.0215C14.849 12.1975 14.7474 12.2856 14.6745 12.3922C14.6099 12.4868 14.562 12.5917 14.5328 12.7024C14.5 12.8274 14.5 12.9618 14.5 13.2306V18.4584C14.5 18.6539 14.5 18.7517 14.4685 18.8363C14.4406 18.911 14.3953 18.9779 14.3363 19.0315C14.2695 19.0922 14.1787 19.1285 13.9971 19.2012L10.5971 20.5612C10.2296 20.7082 10.0458 20.7817 9.89827 20.751C9.76927 20.7242 9.65605 20.6476 9.58325 20.5377C9.5 20.4122 9.5 20.2142 9.5 19.8184V13.2306C9.5 12.9618 9.5 12.8274 9.46715 12.7024C9.43805 12.5917 9.39014 12.4868 9.32551 12.3922C9.25258 12.2856 9.15102 12.1975 8.94789 12.0215L2.55211 6.4785C2.34898 6.30245 2.24742 6.21443 2.17449 6.10776C2.10986 6.01323 2.06195 5.90831 2.03285 5.79756C2 5.67259 2 5.53819 2 5.26939V4.6Z',
              stroke: 'currentColor',
              'stroke-width': '2',
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round'
            })
          ]);
        }
      })
    },
  },
  data() {
    return {
      search: '',
      limit: 0,
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.search = this.pathParameter(this.searchKey);
      this.limit = this.pathParameter(this.limitKey) ? this.pathParameter(this.limitKey) : this.defaultLimit;
    });
  },
  methods: {
    pathParameter(name) {
      if (name = (new RegExp('[?&]' + encodeURIComponent(name) + '=([^&]*)')).exec(location.search))
        return decodeURIComponent(name[1]);
    },
    updateSearch() {
      this.$inertia.get(location.href, {
        [this.searchKey]: this.search,
        [this.limitKey]: this.limit,
        [this.pageKey]: 1, // Update the page to 1 when the search changes
      }, {
        preserveState: this.preserveState
      })
    },
  },
  watch: {
    search: {
      handler: _.throttle(function () {
        this.updateSearch();
      }, () => this.waitUntilSearch),
      deep: true
    },
    limit: {
      handler: _.throttle(function () {
        this.updateSearch();
      }, () => this.waitUntilLimit),
      deep: true
    }
  }
}
</script>
